/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

type TimeSlotGroupProps = {
  label: string;
  slots: readonly string[];
  selectedSlot: string;
  occupiedSlots: Set<string>;
  onSelect: (slot: string) => void;
};

/**
 * Grid of half-hour time chips for morning or afternoon. Occupied slots are
 * disabled and struck through; the selected slot is highlighted.
 *
 * @param props - The group label, slot list, selection, occupied set, and select callback.
 * @returns The time-slot chip grid.
 */
export function TimeSlotGroup({
  label,
  slots,
  selectedSlot,
  occupiedSlots,
  onSelect,
}: TimeSlotGroupProps) {
  return (
    <div>
      <Text
        as="span"
        className="mb-(--space-1) block text-(--text-meta) font-medium text-(--text-tertiary)"
      >
        {label}
      </Text>
      <div className="grid grid-cols-4 gap-(--space-1)">
        {slots.map((slot) => {
          const isOccupied = occupiedSlots.has(slot);
          const isSelected = selectedSlot === slot;
          return (
            <UnstyledButton
              key={slot}
              type="button"
              disabled={isOccupied}
              onClick={() => onSelect(slot)}
              className={cn(
                'rounded-(--radius-sm) px-(--space-1) py-(--space-2)',
                'text-[length:var(--text-meta)] font-medium font-[family-name:var(--font-family)]',
                'transition-[var(--transition-standard)]',
                isOccupied && 'cursor-not-allowed opacity-45 line-through',
                !isOccupied && 'cursor-pointer',
                isSelected
                  ? 'border-[1.5px] border-(--seed-base) bg-(--seed-base) text-(--button-primary-text)'
                  : 'border border-(--border-subtle) bg-(--surface-primary) text-(--text-primary)',
                isOccupied && !isSelected && 'bg-(--surface-tertiary) text-(--text-tertiary)',
              )}
            >
              {slot}
            </UnstyledButton>
          );
        })}
      </div>
    </div>
  );
}
